import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Search, CornerDownLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING } from "@/components/ui-kit";

/**
 * Paleta de comandos do painel (⌘K / Ctrl+K).
 *
 * Existe porque o menu do admin tem 26 itens em 6 seções: chegar em "Importar
 * XML" exigia ler a lista inteira. A busca vale sobre o rótulo E a seção, então
 * "fisc" acha tanto "Fiscal" quanto o que está sob Financeiro.
 *
 * A comparação ignora acentos de propósito — quem digita rápido escreve
 * "orcamento", não "orçamento".
 *
 * Recebe `groups` JÁ filtrado por permissão (o mesmo que a sidebar desenha), e
 * não o menu bruto: senão a paleta viraria um atalho para telas que o perfil não
 * pode abrir, e a regra de acesso passaria a existir em dois lugares.
 */

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/**
 * Todos os termos digitados têm de aparecer em algum campo do item.
 *
 * Para termos de 1–2 letras a comparação exige INÍCIO DE PALAVRA. Em português
 * quase todo plural termina em "-os"/"-as", então buscar "os" por substring
 * trazia Relatórios, Pedidos, Produtos, Orçamentos, Carrinhos, Usuários — oito
 * resultados para duas letras. Com início de palavra, "os" só bate na
 * abreviação declarada de Ordens de Serviço.
 */
function match(item, query) {
  const q = norm(query).split(/\s+/).filter(Boolean);
  if (!q.length) return true;
  const fields = [norm(item.label), norm(item.section), norm(item.to), norm(item.kw)];
  const words = fields.join(" ").split(/[\s/-]+/).filter(Boolean);
  const hay = fields.join(" ");
  return q.every((t) => (t.length <= 2
    ? words.some((w) => w.startsWith(t))
    : hay.includes(t)));
}

/**
 * Ordena por onde o termo bate, não só se bate.
 *
 * Sem isso, "OS" trazia "Orçamentos" na frente de "Ordens de Serviço" — porque
 * `orcamentos` contém `os` como substring. Quem digita "OS" no balcão quer a
 * ordem de serviço, então início de palavra vale mais que meio de palavra.
 */
function score(item, query) {
  const q = norm(query).trim();
  if (!q) return 0;
  const label = norm(item.label);
  const words = label.split(/\s+/);
  const initials = words.map((w) => w[0]).join("");        // "ordens de servico" -> "ods"
  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (words.some((w) => w.startsWith(q))) return 2;
  if (initials.startsWith(q)) return 3;
  // Abreviação DECLARADA ("os" → Ordens de Serviço) tem de vencer acerto
  // acidental de substring no meio de outro rótulo ("orçament(os)").
  const kws = norm(item.kw).split(/\s+/).filter(Boolean);
  if (kws.includes(q)) return 3.2;                          // "nf" → Fiscal, não Importar XML
  if (kws.some((k) => k.startsWith(q))) return 3.5;
  if (label.includes(q)) return 4;
  return 5;                                                 // bateu só na seção ou na rota
}

export default function CommandPalette({ groups = [], open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const items = useMemo(
    () => groups.flatMap((g) => g.items.map((i) => ({ ...i, section: g.section }))),
    [groups],
  );
  const results = useMemo(
    () => items
      .filter((i) => match(i, query))
      // `sort` estável no V8: com pontuação igual, a ordem do menu é mantida.
      .sort((a, b) => score(a, query) - score(b, query)),
    [items, query],
  );

  // Toda abertura começa limpa: a paleta é para ir a algum lugar agora, não
  // para retomar a busca de ontem.
  useEffect(() => {
    if (open) { setQuery(""); setActive(0); }
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  // Mantém a opção ativa visível ao navegar com as setas.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (item) => {
    if (!item) return;
    onOpenChange(false);
    navigate(item.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(results.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" />
        <Dialog.Content
          data-testid="command-palette"
          aria-label="Buscar no painel"
          className="fixed left-1/2 top-[15vh] z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl focus:outline-none"
        >
          <Dialog.Title className="sr-only">Buscar no painel</Dialog.Title>

          <div className="flex items-center gap-3 px-4 border-b border-slate-100">
            <Search size={17} className="text-slate-400 shrink-0" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ir para..."
              aria-label="Buscar tela do painel"
              role="combobox"
              aria-expanded
              aria-controls="cmdk-list"
              aria-activedescendant={results[active] ? `cmdk-opt-${active}` : undefined}
              autoFocus
              data-testid="command-palette-input"
              data-ring=""
              className="flex-1 h-14 text-[15px] bg-transparent outline-none placeholder:text-slate-400"
            />
            <kbd className="hidden sm:block shrink-0 text-[10px] font-semibold text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">ESC</kbd>
          </div>

          <ul id="cmdk-list" role="listbox" aria-label="Telas do painel" ref={listRef}
            className="max-h-[52vh] overflow-y-auto scrollbar-thin py-2">
            {results.map((item, i) => (
              <li
                key={item.to}
                id={`cmdk-opt-${i}`}
                role="option"
                aria-selected={i === active}
                data-active={i === active}
                data-testid="command-palette-option"
                onMouseDown={(e) => { e.preventDefault(); go(item); }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "flex items-center gap-3 mx-2 px-3 h-11 rounded-lg cursor-pointer text-sm",
                  i === active ? "bg-brand-50 text-brand-800" : "text-slate-700",
                )}
              >
                <item.icon size={16} className={i === active ? "text-brand-600" : "text-slate-400"} aria-hidden />
                <span className="font-medium truncate">{item.label}</span>
                <span className="ml-auto text-[11px] uppercase tracking-wider text-slate-400 shrink-0">{item.section}</span>
                {i === active && <CornerDownLeft size={13} className="text-brand-400 shrink-0" aria-hidden />}
              </li>
            ))}
            {results.length === 0 && (
              <li className="px-5 py-8 text-center text-sm text-slate-400">
                Nada encontrado para “{query}”.
              </li>
            )}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Botão que abre a paleta, com o atalho visível.
 *
 * O atalho só ensina se estiver à vista: os F2/F4 do PDV existem há tempo e
 * ninguém descobriu, porque estavam em letra miúda dentro de um botão.
 */
export function CommandPaletteTrigger({ onClick }) {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="command-palette-trigger"
      data-ring=""
      className={cn(
        "flex items-center gap-2 h-9 pl-3 pr-2 rounded-lg border border-slate-200 bg-white",
        "text-sm text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors",
        FOCUS_RING,
      )}
    >
      <Search size={15} aria-hidden />
      <span className="hidden sm:inline">Buscar tela</span>
      <kbd className="hidden sm:inline text-[10px] font-semibold border border-slate-200 rounded px-1.5 py-0.5 ml-1">
        {isMac ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}
